import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { Box, DropdownSingleSelect, IDropdownSingleSelectProps, Text } from '@opendoor/bricks/core';

import { ERROR_PAGE_ROUTE } from '../../src/routes/routes';
import {
  GET_PARTNER_OPTIONS,
  IGetPartnerOptions,
  IGetPartnerOptionsVariables,
} from '../graphql/queries/GetPartnerOptions';
// eslint-disable-next-line import/no-deprecated
import { UNSAFE_ISingleLineInputProps, UNSAFE_SingleLineInput } from './UNSAFE_SingleLineInput';

export type PartnerOptionsProps = {
  partnerIdentifier?: string;
  partnershipType: string;
  onChange: IDropdownSingleSelectProps['onChange'];
  otherPartnerIdentifier?: string;
  onOtherChange: UNSAFE_ISingleLineInputProps['onChange'];
  title: string;
  placeholder: string;
  showLoader: (isLoading: boolean) => void;
};

type Option = {
  value: string;
  label: string;
};

const PartnerOptions: React.FC<PartnerOptionsProps> = (props: PartnerOptionsProps) => {
  const [partnerOptions, setPartnerOptions] = useState(Array<Option>());

  const { loading, error, data } = useQuery<IGetPartnerOptions, IGetPartnerOptionsVariables>(
    GET_PARTNER_OPTIONS,
    {
      variables: { partnershipType: props.partnershipType },
    },
  );

  useEffect(() => {
    props.showLoader(loading);
    // eslint-disable-next-line
  }, [loading]);

  useEffect(() => {
    if (data) {
      setPartnerOptions(
        data.partnersByType
          .map((partner: any) => {
            if (partner.identifier === 'other_builder') {
              return {
                value: partner.identifier,
                label: 'Other', // Replace "Other Builder" with "other"
              };
            }
            return {
              value: partner.identifier,
              label: partner.displayName,
            };
          })
          .sort((partnerA: any, partnerB: any) => {
            return partnerA.label > partnerB.label ? 1 : -1;
          }),
      );
    }
  }, [data]);

  if (error) {
    return <Redirect to={ERROR_PAGE_ROUTE} />;
  }

  const selectedValue =
    props.partnerIdentifier &&
    partnerOptions.some((partner) => partner.value.toLowerCase() === props.partnerIdentifier)
      ? [props.partnerIdentifier]
      : undefined;

  return loading ? null : (
    <Box marginBottom={5}>
      <Text as="label" id="partner" fontWeight="bold" mb={2}>
        {props.title}
      </Text>
      <DropdownSingleSelect
        aria-labelledby="partner"
        onChange={props.onChange}
        options={partnerOptions}
        placeholder={props.placeholder}
        selectedValue={selectedValue}
      />
      {props.partnerIdentifier === 'other_builder' && (
        <Box mt={2}>
          {/* eslint-disable-next-line react/jsx-pascal-case */}
          <UNSAFE_SingleLineInput
            name="other-partner-input"
            placeholder={'Please specify'}
            content={props.otherPartnerIdentifier}
            onChange={props.onOtherChange}
            unboundedWidth={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default PartnerOptions;
